import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import { isPartnerScopedRole } from '../lib/authorization';
import { Eye, FileText, X, RefreshCcw, ChevronDown, ChevronRight, Calendar, Trash2 } from 'lucide-react';

const DailyReportsList = ({ role, partner, userCompanyId }) => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingDate, setDeletingDate] = useState(null);
  const [viewingReport, setViewingReport] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({ company_id: '', startDate: '', endDate: '' });
  const [companies, setCompanies] = useState([]);
  const [expandedDates, setExpandedDates] = useState({});

  useEffect(() => {
    // Load companies for filter
    const loadCompanies = async () => {
      let query = supabase.from('companies').select('id, name').eq('status', 'Ativo');
      if (isPartnerScopedRole(role) && partner?.id) {
        const [{ data: pData }, { data: cData }] = await Promise.all([
          supabase.from('profiles').select('company_id').eq('sas_partner_id', partner.id),
          supabase.from('classes').select('company_id').eq('partner_id', partner.id)
        ]);
        const compIdsFromProfiles = pData ? pData.map(p => p.company_id).filter(Boolean) : [];
        const compIdsFromClasses = cData ? cData.map(c => c.company_id).filter(Boolean) : [];
        let compIds = [...new Set([...compIdsFromProfiles, ...compIdsFromClasses])];
        
        if (userCompanyId) {
          compIds = compIds.filter(id => id !== userCompanyId);
        }

        if (compIds.length > 0) {
          query = query.in('id', compIds);
        } else {
          query = query.in('id', ['00000000-0000-0000-0000-000000000000']);
        }
      }
      const { data } = await query;
      if (data) setCompanies(data);
    };
    loadCompanies();
  }, [role, partner]);

  useEffect(() => {
    const loadReports = async () => {
      setLoading(true);
      try {
        let query = supabase
          .from('admin_daily_reports')
          .select('id, title, created_at, status, data_payload, company_id, companies(name)')
          .order('created_at', { ascending: false });

        if (isPartnerScopedRole(role) && partner?.id) {
          query = query.eq('sas_partner_id', partner.id);
        }

        if (filters.company_id) query = query.eq('company_id', filters.company_id);
        if (filters.startDate) query = query.gte('created_at', `${filters.startDate}T00:00:00`);
        if (filters.endDate) query = query.lte('created_at', `${filters.endDate}T23:59:59`);

        const { data, error } = await query;
        if (error) throw error;
        setReports(data || []);
      } catch (e) {
        console.error("Error loading daily reports", e);
      } finally {
        setLoading(false);
      }
    };
    loadReports();
  }, [filters, role, partner]);

  const openReport = async (report) => {
      // Fetch full content only when opening
      const { data, error } = await supabase
        .from('admin_daily_reports')
        .select('html_content')
        .eq('id', report.id)
        .single();
      
      if (data) {
        setViewingReport({ ...report, html_content: data.html_content });
      } else {
        setError("Erro ao carregar conteúdo do relatório.");
      }
  };

  const groupedReports = reports.reduce((acc, report) => {
    const date = new Date(report.created_at).toLocaleDateString('pt-BR');
    if (!acc[date]) acc[date] = [];
    acc[date].push(report);
    return acc;
  }, {});

  const toggleDate = (date) => {
    setExpandedDates(prev => ({ ...prev, [date]: !prev[date] }));
  };

  const handleDeleteDay = (e, dateStr, count) => {
    e.stopPropagation();
    setConfirmDelete({ dateStr, count });
  };

  const executeDelete = async () => {
    if (!confirmDelete) return;
    const { dateStr, count } = confirmDelete;
    
    setConfirmDelete(null);
    setDeletingDate(dateStr);
    
    try {
        // Convert dd/mm/yyyy to yyyy-mm-dd
        const [day, month, year] = dateStr.split('/');
        const isoDate = `${year}-${month}-${day}`;

        // Call RPC to delete reports for this date
        // If RPC is not available yet, fallback to direct delete (less efficient but works)
        let error = null;
        try {
           const { error: rpcError } = await supabase.rpc('delete_daily_reports_by_date', { p_date: isoDate });
           error = rpcError;
        } catch (e) {
           error = e;
        }
        
        if (error) {
            // Fallback: try delete directly if RPC fails (e.g. permission issue or not deployed)
            console.warn("RPC failed, trying direct delete...", error);
            
            // Need to build date range because created_at is timestamp
            const start = `${isoDate}T00:00:00`;
            const end = `${isoDate}T23:59:59`;
            
            const { error: delError } = await supabase
                .from('admin_daily_reports')
                .delete()
                .gte('created_at', start)
                .lte('created_at', end);
            
            if (delError) throw delError;
        }

        // Update local state by removing reports for this date
        setReports(prev => prev.filter(r => {
            const rDate = new Date(r.created_at).toLocaleDateString('pt-BR');
            return rDate !== dateStr;
        }));
        
        // Remove from expanded if needed
        setExpandedDates(prev => {
             const next = { ...prev };
             delete next[dateStr];
             return next;
        });

        // alert('Relatórios excluídos com sucesso.'); // Optional feedback
    } catch (err) {
        console.error("Error deleting reports:", err);
        setError(`Erro ao excluir relatórios: ${err.message}`);
    } finally {
        setDeletingDate(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Modal de Erro */}
      {error && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4 animate-fade-in">
          <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-md shadow-2xl p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-white">Erro</h3>
              <button onClick={() => setError(null)} className="text-slate-500 hover:text-white"><X size={20} /></button>
            </div>
            <div className="text-slate-300">{error}</div>
            <div className="flex justify-end mt-6">
              <button onClick={() => setError(null)} className="bg-slate-800 hover:bg-slate-700 px-6 py-2 rounded-lg text-white font-bold shadow-lg transition">Fechar</button>
            </div>
          </div>
        </div>
      )}
      {/* Modal de Confirmação de Exclusão */}
      {confirmDelete && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4 animate-fade-in">
          <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-md shadow-2xl p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <Trash2 className="text-rose-500" size={20} />
                Confirmar Exclusão
              </h3>
              <button onClick={() => setConfirmDelete(null)} className="text-slate-500 hover:text-white"><X size={20} /></button>
            </div>
            <div className="text-slate-300 space-y-4">
              <p>Tem certeza que deseja excluir <strong>TODOS</strong> os {confirmDelete.count} relatórios de <strong>{confirmDelete.dateStr}</strong>?</p>
              <div className="bg-rose-500/10 border border-rose-500/20 p-3 rounded-lg text-rose-200 text-sm">
                 Atenção: Esta ação não pode ser desfeita e permitirá que a rotina diária seja executada novamente para esta data.
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button 
                onClick={() => setConfirmDelete(null)} 
                className="px-4 py-2 text-slate-400 hover:text-white transition font-bold text-sm"
              >
                Cancelar
              </button>
              <button 
                onClick={executeDelete} 
                className="bg-rose-600 hover:bg-rose-500 px-6 py-2 rounded-lg text-white font-bold shadow-lg transition flex items-center gap-2"
              >
                <Trash2 size={16} />
                Excluir Definitivamente
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex gap-4 flex-wrap bg-slate-950/50 p-4 rounded-lg border border-slate-800">
        <select 
           value={filters.company_id} 
           onChange={e => setFilters({...filters, company_id: e.target.value})}
           className="bg-slate-900 border border-slate-800 rounded-lg px-3 py-2 text-xs text-white outline-none focus:border-slate-600 transition"
         >
            <option value="">Todas as Empresas</option>
            {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
         </select>
         <input 
            type="date" 
            value={filters.startDate}
            onChange={e => setFilters({...filters, startDate: e.target.value})}
            className="bg-slate-900 border border-slate-800 rounded-lg px-3 py-2 text-white text-xs outline-none [color-scheme:dark]"
         />
         <input 
            type="date" 
            value={filters.endDate}
            onChange={e => setFilters({...filters, endDate: e.target.value})}
            className="bg-slate-900 border border-slate-800 rounded-lg px-3 py-2 text-white text-xs outline-none [color-scheme:dark]"
         />
      </div>

      <div className="rounded-lg overflow-hidden border border-slate-800 bg-slate-900/50">
        {loading ? (
          <div className="p-8 text-center text-slate-500 flex flex-col items-center gap-2">
            <RefreshCcw size={24} className="animate-spin text-orange-500" />
            Carregando relatórios...
          </div>
        ) : Object.keys(groupedReports).length > 0 ? (
          <div className="flex flex-col">
            {Object.entries(groupedReports).map(([date, dayReports]) => (
              <div key={date} className="border-b border-slate-800 last:border-0">
                <button 
                  onClick={() => toggleDate(date)}
                  className="w-full flex items-center justify-between p-4 hover:bg-slate-800 transition group"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-slate-800 flex items-center justify-center text-slate-400 group-hover:text-orange-400 group-hover:bg-orange-400/10 transition-colors">
                      {expandedDates[date] ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar size={16} className="text-orange-500" />
                      <span className="font-bold text-white">{date}</span>
                      <span className="text-xs text-slate-500 bg-slate-800 px-2 py-0.5 rounded-full ml-2">
                        {dayReports.length} {dayReports.length === 1 ? 'relatório' : 'relatórios'}
                      </span>
                      
                      <button 
                         onClick={(e) => handleDeleteDay(e, date, dayReports.length)}
                         disabled={deletingDate === date}
                         className="p-1.5 ml-3 text-slate-600 hover:text-rose-500 hover:bg-rose-500/10 rounded-full transition opacity-0 group-hover:opacity-100 disabled:opacity-50 disabled:cursor-not-allowed"
                         title="Excluir relatórios desta data (permite regeração)"
                      >
                         {deletingDate === date ? <RefreshCcw size={14} className="animate-spin" /> : <Trash2 size={14} />}
                      </button>
                    </div>
                  </div>
                </button>

                {expandedDates[date] && (
                  <div className="overflow-x-auto bg-slate-950/30 animate-fade-in">
                    <table className="w-full text-left">
                      <thead className="bg-slate-950/80 text-slate-500 text-[10px] uppercase font-bold tracking-wider">
                        <tr>
                          <th className="p-4 py-2 pl-16">Data/Hora</th>
                          <th className="p-4 py-2">Título</th>
                          <th className="p-4 py-2">Empresa</th>
                          <th className="p-4 py-2">Alunos</th>
                          <th className="p-4 py-2">Cursos Ativos</th>
                          <th className="p-4 py-2">Status</th>
                          <th className="p-4 py-2 text-right">Ações</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-800/50 text-sm">
                        {dayReports.map((report) => (
                          <tr key={report.id} className="hover:bg-slate-800/50 transition group">
                            <td className="p-4 pl-16 text-slate-400 text-xs">
                              {new Date(report.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                            </td>
                            <td className="p-4 text-white font-medium">{report.title}</td>
                            <td className="p-4 text-slate-300 text-xs">{report.companies?.name || '-'}</td>
                            <td className="p-4 text-slate-300 text-xs">{report.data_payload?.metrics?.total_alunos || 0}</td>
                            <td className="p-4 text-slate-300 text-xs">{report.data_payload?.metrics?.total_cursos || 0}</td>
                            <td className="p-4">
                               <span className={`px-2 py-1 rounded text-[10px] font-bold uppercase ${report.status === 'generated' ? 'bg-orange-500/10 text-orange-400 border border-orange-500/20' : 'bg-rose-500/10 text-rose-400 border border-rose-500/20'}`}>
                                 {report.status}
                               </span>
                            </td>
                            <td className="p-4 text-right">
                              <button 
                                 onClick={() => openReport(report)}
                                 className="text-orange-400 hover:text-orange-300 text-xs font-bold uppercase tracking-wider flex items-center gap-1 ml-auto group-hover:scale-105 transition-transform"
                               >
                                 <Eye size={14} /> Visualizar
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="p-12 text-center text-slate-500">
            Nenhum relatório encontrado.
          </div>
        )}
      </div>

      {viewingReport && (
        <div className="fixed inset-0 bg-slate-950/90 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fade-in">
          <div className="bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-4xl shadow-2xl overflow-hidden flex flex-col h-[90vh]">
            <div className="p-4 border-b border-slate-800 flex justify-between items-center bg-slate-950">
               <h3 className="text-lg font-bold text-white flex items-center gap-2">
                 <FileText size={18} className="text-orange-500" /> 
                 Pré-visualização de E-mail
               </h3>
               <button onClick={() => setViewingReport(null)} className="text-slate-500 hover:text-white transition">
                 <X size={24} />
               </button>
            </div>
            <div className="flex-1 overflow-auto p-8 bg-slate-100 flex justify-center">
               <div 
                 className="bg-white shadow-lg w-full max-w-[800px] min-h-[600px]"
                 dangerouslySetInnerHTML={{ __html: viewingReport.html_content }}
               />
            </div>
            <div className="p-4 border-t border-slate-800 bg-slate-950 flex justify-end gap-3">
               <button 
                 onClick={() => setViewingReport(null)}
                 className="px-4 py-2 text-slate-400 hover:text-white transition font-bold text-sm"
               >
                 Fechar
               </button>
               {/* Future: Add Send Email / Download PDF buttons here */}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DailyReportsList;
